import React, { useState, useEffect, useMemo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Plus, Search, ChevronDown, ChevronRight, ShieldAlert, Trash2 } from 'lucide-react';
import { roleAPI, userAPI } from '../../services/api';
import { Switch, FullSheet, ConfirmSheet, UsersTabs, fabStyle, sectionLabel, groupBox } from './kit';
import { actionKind, KIND_META, isCritical, countRights, newlyGrantedCritical } from '../../utils/userAccess';

const emptyRights = (mod) => Object.fromEntries(mod.actions.map(a => [a.key, false]));
const fullRights = (mod) => Object.fromEntries(mod.actions.map(a => [a.key, true]));
const buildRights = (role, registry) => {
  const base = {};
  for (const mod of registry) base[mod.key] = { ...emptyRights(mod), ...(role.rights?.[mod.key] || {}) };
  return base;
};
const apiErr = (err, fallback) => err.response?.data?.message || fallback;

function ModuleCard({ mod, rights, onToggle, onAll, query }) {
  const [open, setOpen] = useState(false);
  const on = mod.actions.filter(a => rights[mod.key]?.[a.key]).length;
  const expanded = open || !!query;
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 56, padding: '0 14px' }}>
        <button type="button" aria-expanded={expanded} onClick={() => setOpen(o => !o)}
          style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', textAlign: 'left', color: 'var(--text)', minHeight: 56, cursor: 'pointer' }}>
          {expanded ? <ChevronDown size={16} color="var(--text-muted)" /> : <ChevronRight size={16} color="var(--text-muted)" />}
          <span style={{ minWidth: 0 }}>
            <span style={{ display: 'block', fontWeight: 600, fontSize: '.9rem' }}>{mod.label}</span>
            <span style={{ display: 'block', fontSize: '.72rem', color: on ? 'var(--primary, #2563eb)' : 'var(--text-muted)' }}>{on === 0 ? 'No access' : `${on} of ${mod.actions.length} allowed`}</span>
          </span>
        </button>
        <Switch label={`All ${mod.label} rights`} checked={on === mod.actions.length} onChange={(v) => onAll(mod, v)} />
      </div>
      {expanded && (
        <div style={{ background: 'var(--bg)', padding: '2px 0' }}>
          {mod.actions.map(a => {
            const kind = KIND_META[actionKind(a.key)];
            const crit = isCritical(mod.key, a.key);
            return (
              <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 48, padding: '0 14px 0 38px' }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: '.84rem' }}>
                  {a.label}
                  {crit && <ShieldAlert size={13} color="#dc2626" style={{ marginLeft: 6, verticalAlign: '-2px' }} aria-label="Controls other people's access" />}
                </span>
                <span style={{ fontSize: '.64rem', fontWeight: 700, color: kind.color, border: `1px solid ${kind.color}`, borderRadius: 8, padding: '1px 6px' }}>{kind.label}</span>
                <Switch label={a.label} checked={!!rights[mod.key]?.[a.key]} onChange={() => onToggle(mod.key, a.key)} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RoleEditor({ role, registry, onClose, onSaved, onDeleted, askConfirm }) {
  const isNew = !role._id;
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description || '');
  const [rights, setRights] = useState(() => buildRights(role, registry));
  const [initial] = useState(() => JSON.stringify([role.name, role.description || '', buildRights(role, registry)]));
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [nameErr, setNameErr] = useState('');
  const dirty = JSON.stringify([name, description, rights]) !== initial;

  const sections = useMemo(() => {
    const by = {};
    for (const mod of registry) {
      if (mod.section === 'FIELD AGENT APP') continue; // per-agent, set on the user, not here
      if (query && !mod.label.toLowerCase().includes(query.toLowerCase())) continue;
      (by[mod.section] = by[mod.section] || []).push(mod);
    }
    return Object.entries(by);
  }, [registry, query]);

  const toggle = (m, a) => setRights(r => ({ ...r, [m]: { ...r[m], [a]: !r[m][a] } }));
  const setAll = (mod, v) => setRights(r => ({ ...r, [mod.key]: v ? fullRights(mod) : emptyRights(mod) }));
  const counts = countRights(rights, registry);

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = { name: name.trim(), description, rights };
      const { data } = isNew ? await roleAPI.create(payload) : await roleAPI.update(role._id, payload);
      toast.success(`Role "${name.trim()}" ${isNew ? 'created' : 'updated'}`);
      onSaved(data.data);
    } catch (err) { toast.error(apiErr(err, 'Failed to save role')); }
    finally { setSaving(false); }
  };

  const save = () => {
    if (!name.trim()) { setNameErr('Give the role a name.'); return; }
    const crit = newlyGrantedCritical(role.rights, rights);
    const lines = [];
    if (!isNew && role.userCount > 0) lines.push(`This changes access for ${role.userCount} user${role.userCount === 1 ? '' : 's'} who have this role.`);
    if (crit.length) lines.push(`It lets them manage other people's access (${crit.map(c => c.split('.')[1]).join(', ')}).`);
    if (lines.length) askConfirm({ title: 'Save these changes?', body: lines.join('\n\n'), yes: 'Save', danger: crit.length > 0, onYes: doSave });
    else doSave();
  };

  const close = () => (dirty ? askConfirm({ title: 'Discard changes?', body: 'You have unsaved changes to this role.', yes: 'Discard', danger: true, onYes: onClose }) : onClose());

  const bulk = (full) => askConfirm({
    title: full ? 'Grant full access?' : 'Revoke all rights?', danger: full, yes: full ? 'Grant everything' : 'Revoke all',
    body: full ? 'Every permission in this role will be switched on, including managing users and roles. Nothing is saved until you tap Save.' : 'Every permission will be switched off. Nothing is saved until you tap Save.',
    onYes: () => { const n = {}; for (const mod of registry) n[mod.key] = full ? fullRights(mod) : emptyRights(mod); setRights(n); },
  });

  const remove = () => askConfirm({
    title: `Delete role "${role.name}"?`, danger: true, yes: 'Delete role', body: 'This cannot be undone.',
    onYes: async () => { try { await roleAPI.delete(role._id); toast.success('Role deleted'); onDeleted(); } catch (err) { toast.error(apiErr(err, 'Failed to delete role')); } },
  });

  return (
    <FullSheet title={isNew ? 'New role' : role.name} onClose={close}
      footer={<button type="button" className="btn btn-primary" style={{ width: '100%', minHeight: 48 }} disabled={saving || (!isNew && !dirty)} onClick={save}>{saving ? 'Saving…' : isNew ? 'Create role' : dirty ? 'Save changes' : 'No changes'}</button>}>
      <div style={groupBox}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
          <label style={{ display: 'block', fontSize: '.74rem', fontWeight: 600, color: 'var(--text-muted)' }}>Role name</label>
          <input value={name} onChange={e => { setName(e.target.value); setNameErr(''); }} placeholder="e.g. Office Staff"
            style={{ width: '100%', border: 'none', background: 'none', outline: 'none', color: 'var(--text)', minHeight: 30, fontWeight: 600 }} />
          {nameErr && <div role="alert" style={{ color: 'var(--danger, #dc2626)', fontSize: '.78rem' }}>{nameErr}</div>}
        </div>
        <div style={{ padding: '10px 14px' }}>
          <label style={{ display: 'block', fontSize: '.74rem', fontWeight: 600, color: 'var(--text-muted)' }}>Description (optional)</label>
          <input value={description} onChange={e => setDescription(e.target.value)} style={{ width: '100%', border: 'none', background: 'none', outline: 'none', color: 'var(--text)', minHeight: 30 }} />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 2px 8px' }}>
        <span style={{ flex: 1, fontSize: '.84rem', fontWeight: 700 }}>{counts.on} of {counts.total} permissions on</span>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => bulk(false)}>Revoke all</button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => bulk(true)}>Full access</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 12px' }}>
        <Search size={16} color="var(--text-muted)" />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Find a permission area" aria-label="Find a permission area"
          style={{ flex: 1, border: 'none', background: 'none', outline: 'none', color: 'var(--text)', minHeight: 44 }} />
      </div>
      <div className="text-muted" style={{ fontSize: '.72rem', margin: '8px 4px 0' }}>
        Each right is tagged View, Create, Edit, Delete or Admin. <ShieldAlert size={12} color="#dc2626" style={{ verticalAlign: '-2px' }} /> marks rights that control other people's access.
      </div>

      {sections.map(([section, mods]) => (
        <div key={section}>
          <div style={sectionLabel}>{section}</div>
          <div style={groupBox}>{mods.map(mod => <ModuleCard key={mod.key} mod={mod} rights={rights} query={query} onToggle={toggle} onAll={setAll} />)}</div>
        </div>
      ))}
      {sections.length === 0 && <div className="text-muted" style={{ textAlign: 'center', padding: 24 }}>No permission area matches.</div>}

      {!isNew && (
        <button type="button" className="btn btn-danger" style={{ width: '100%', minHeight: 48, marginTop: 20 }} disabled={role.userCount > 0} onClick={remove}>
          <Trash2 size={15} /> {role.userCount > 0 ? `Delete (${role.userCount} user${role.userCount === 1 ? ' has' : 's have'} this role)` : 'Delete role'}
        </button>
      )}
    </FullSheet>
  );
}

export default function RolesMobile() {
  const [roles, setRoles] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new, object = existing
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, reg] = await Promise.all([roleAPI.getAll(), userAPI.getModuleRegistry()]);
      setRoles(r.data.data || []); setRegistry(reg.data.data || []);
    } catch { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const list = roles.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const done = () => { setEditing(undefined); load(); };

  return (
    <div>
      <UsersTabs active="roles" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: '0 12px', marginBottom: 10 }}>
        <Search size={16} color="var(--text-muted)" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search roles" aria-label="Search roles"
          style={{ flex: 1, border: 'none', background: 'none', outline: 'none', color: 'var(--text)', minHeight: 44 }} />
      </div>
      <div className="text-muted" style={{ fontSize: '.76rem', margin: '0 4px 10px' }}>A role is a reusable set of rights. Give one to each staff, technical or co-admin user. Admins always have full access.</div>

      {loading ? <div style={{ textAlign: 'center', padding: 36 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        : list.length === 0 ? <div className="text-muted" style={{ textAlign: 'center', padding: 32 }}>{roles.length ? 'No roles match.' : 'No roles yet. Tap + to create one.'}</div> : (
          <div style={groupBox}>
            {list.map(r => {
              const c = countRights(r.rights, registry);
              return (
                <button key={r._id} type="button" onClick={() => setEditing(r)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, minHeight: 64, padding: '8px 14px', border: 'none', borderBottom: '1px solid var(--border)', background: 'none', textAlign: 'left', color: 'var(--text)', cursor: 'pointer' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '.92rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                    <div className="text-muted" style={{ fontSize: '.76rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.description || `${c.on} of ${c.total} rights`}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span className="badge badge-info" style={{ fontSize: '.68rem' }}>{r.userCount} user{r.userCount === 1 ? '' : 's'}</span>
                    {r.description && <div className="text-muted" style={{ fontSize: '.7rem', marginTop: 2 }}>{c.on}/{c.total} rights</div>}
                  </div>
                </button>
              );
            })}
          </div>
        )}

      <button type="button" aria-label="New role" style={fabStyle} onClick={() => setEditing(null)}><Plus size={26} /></button>

      {editing !== undefined && registry.length > 0 && (
        <RoleEditor role={editing || { _id: null, name: '', description: '', rights: {}, userCount: 0 }} registry={registry} askConfirm={setConfirm}
          onClose={() => setEditing(undefined)} onSaved={done} onDeleted={done} />
      )}
      <ConfirmSheet confirm={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
